<template>
  <div class="app-class-category-selector full-width">
    <div class="app-modal-field">
      <div class="app-class-category-selector__label-row">
        <span>Clase *</span>
        <button type="button" class="app-class-category-selector__link" :disabled="disabled" @click="toggleClassInlineForm">
          + Nueva clase
        </button>
      </div>

      <multiselect
        v-model="classOptionModel"
        :options="classOptions"
        track-by="value"
        label="label"
        :show-labels="false"
        :searchable="true"
        :allow-empty="true"
        :disabled="disabled"
        :max-height="240"
        placeholder="Selecciona clase"
        class="app-modal-select app-select"
        @close="$emit('touch-class-option')"
      >
        <template #noResult><span>Sin opción disponible</span></template>
        <template #noOptions><span>Sin opción disponible</span></template>
      </multiselect>
      <div class="invalid-feedback d-block" v-if="classValidation?.$error">
        <span v-if="classValidation.$errors.length">{{ classValidation.$errors[0].$message }}</span>
      </div>

      <div v-if="showNewClassInput" class="app-class-category-selector__inline">
        <label class="app-modal-field">
          <span>Nombre de la clase *</span>
          <input
            v-model.trim="newClassName"
            type="text"
            class="form-control"
            :disabled="disabled"
            placeholder="Nombre de la clase"
          />
        </label>

        <div class="app-class-category-selector__actions">
          <button type="button" class="btn btn-light" :disabled="disabled" @click="cancelNewClass">Cancelar</button>
          <button
            type="button"
            class="app-header-btn app-header-btn--primary"
            :disabled="disabled || creatingClass || newClassName === ''"
            @click="saveNewClass"
          >
            Guardar y usar clase
          </button>
        </div>
      </div>
    </div>

    <div class="app-modal-field">
      <div class="app-class-category-selector__label-row">
        <span>Categoría *</span>
        <button type="button" class="app-class-category-selector__link" :disabled="disabled" @click="toggleCategoryInlineForm">
          + Nueva categoría
        </button>
      </div>

      <multiselect
        v-model="categoryOptionModel"
        :options="categoryOptions"
        track-by="value"
        label="label"
        :show-labels="false"
        :searchable="true"
        :allow-empty="true"
        :disabled="disabled"
        :max-height="240"
        placeholder="Selecciona categoría"
        class="app-modal-select app-select"
        @close="$emit('touch-category-option')"
      >
        <template #noResult><span>Sin opción disponible</span></template>
        <template #noOptions><span>Sin opción disponible</span></template>
      </multiselect>
      <div class="invalid-feedback d-block" v-if="categoryValidation?.$error">
        <span v-if="categoryValidation.$errors.length">{{ categoryValidation.$errors[0].$message }}</span>
      </div>

      <div v-if="showNewCategoryInput" class="app-class-category-selector__inline">
        <label class="app-modal-field">
          <span>Nombre de la categoría *</span>
          <input
            v-model.trim="newCategoryName"
            type="text"
            class="form-control"
            :disabled="disabled"
            placeholder="Nombre de la categoría"
          />
        </label>

        <div class="app-class-category-selector__actions">
          <button type="button" class="btn btn-light" :disabled="disabled" @click="cancelNewCategory">Cancelar</button>
          <button
            type="button"
            class="app-header-btn app-header-btn--primary"
            :disabled="disabled || creatingCategory || newCategoryName === ''"
            @click="saveNewCategory"
          >
            Guardar y usar categoría
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import expenseClassesService from '@/services/expenseClasses';
import expenseCategoriesService from '@/services/expenseCategories';
import { normalizeCatalogOptions } from '@/utils/catalogOptions';
import { extractCollection, extractPayload } from '@/utils/dataNormalization';
import { resolveOption, toPositiveOptionId } from '@/utils/optionValues';

const extractCreatedItem = (response) => {
  const payload = extractPayload(response);

  if (payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object') {
    return payload.data;
  }

  return payload && typeof payload === 'object' ? payload : null;
};

export default {
  name: 'AppClassCategorySelector',
  props: {
    classOption: {
      type: [Object, Number, String, null],
      default: null,
    },
    categoryOption: {
      type: [Object, Number, String, null],
      default: null,
    },
    classValidation: {
      type: Object,
      default: null,
    },
    categoryValidation: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:classOption', 'update:categoryOption', 'touch-class-option', 'touch-category-option'],
  data() {
    return {
      classOptions: [],
      allCategoryOptions: [],
      categoryOptions: [],
      loadingCatalogs: false,
      showNewClassInput: false,
      newClassName: '',
      creatingClass: false,
      showNewCategoryInput: false,
      newCategoryName: '',
      creatingCategory: false,
    };
  },
  computed: {
    classOptionModel: {
      get() {
        return resolveOption(this.classOptions, this.classOption);
      },
      set(value) {
        this.$emit('update:classOption', value || null);
      },
    },
    categoryOptionModel: {
      get() {
        return resolveOption(this.categoryOptions, this.categoryOption);
      },
      set(value) {
        this.$emit('update:categoryOption', value || null);
      },
    },
  },
  watch: {
    classOption: {
      immediate: true,
      handler() {
        this.handleClassChanged();
      },
    },
  },
  mounted() {
    this.loadCatalogs();
  },
  methods: {
    async loadCatalogs() {
      if (this.loadingCatalogs) {
        return;
      }

      this.loadingCatalogs = true;

      try {
        const [classesRes, categoriesRes] = await Promise.allSettled([
          expenseClassesService.selectors(),
          expenseCategoriesService.selectors(),
        ]);

        this.classOptions = normalizeCatalogOptions(classesRes.status === 'fulfilled' ? extractCollection(classesRes.value) : [], { useCodeInLabel: false });
        this.allCategoryOptions = normalizeCatalogOptions(categoriesRes.status === 'fulfilled' ? extractCollection(categoriesRes.value) : [], { useCodeInLabel: false });
        this.categoryOptions = [...this.allCategoryOptions];

        this.syncResolvedValue();
        await this.handleClassChanged();
      } finally {
        this.loadingCatalogs = false;
      }
    },
    syncResolvedValue() {
      const resolvedClass = resolveOption(this.classOptions, this.classOption);
      if (resolvedClass && resolvedClass !== this.classOption) {
        this.$emit('update:classOption', resolvedClass);
      }

      const resolvedCategory = resolveOption(this.categoryOptions, this.categoryOption);
      if (resolvedCategory && resolvedCategory !== this.categoryOption) {
        this.$emit('update:categoryOption', resolvedCategory);
      }
    },
    async handleClassChanged() {
      const classId = toPositiveOptionId(this.classOption);

      if (classId <= 0) {
        this.categoryOptions = [...this.allCategoryOptions];
        this.syncResolvedValue();
        return;
      }

      try {
        const categoriesResult = await expenseCategoriesService.selectors({ class_id: classId });
        this.categoryOptions = normalizeCatalogOptions(extractCollection(categoriesResult), { useCodeInLabel: false });

        const selectedCategoryValue = toPositiveOptionId(this.categoryOption);
        if (selectedCategoryValue > 0) {
          const exists = this.categoryOptions.some((option) => toPositiveOptionId(option) === selectedCategoryValue);
          if (!exists) {
            this.$emit('update:categoryOption', null);
          }
        }

        this.syncResolvedValue();
      } catch (error) {
        this.categoryOptions = [];
      }
    },
    toggleClassInlineForm() {
      if (this.disabled) {
        return;
      }

      this.showNewClassInput = !this.showNewClassInput;
      if (!this.showNewClassInput) {
        this.newClassName = '';
      }
    },
    async saveNewClass() {
      if (this.disabled) {
        return;
      }

      const name = String(this.newClassName || '').trim();
      if (name === '' || this.creatingClass) {
        return;
      }

      this.creatingClass = true;
      try {
        const response = await expenseClassesService.store({ name });
        const created = extractCreatedItem(response);
        const createdOption = normalizeCatalogOptions([created], { useCodeInLabel: false })[0] || null;

        if (createdOption) {
          this.classOptions = [...this.classOptions, createdOption];
          this.$emit('update:classOption', createdOption);
        }

        this.showNewClassInput = false;
        this.newClassName = '';
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'No fue posible crear la clase.';
        this.$toast?.error(message);
      } finally {
        this.creatingClass = false;
      }
    },
    cancelNewClass() {
      if (this.disabled) {
        return;
      }

      this.showNewClassInput = false;
      this.newClassName = '';
    },
    toggleCategoryInlineForm() {
      if (this.disabled) {
        return;
      }

      const classId = toPositiveOptionId(this.classOption);
      if (classId <= 0) {
        this.$toast?.error('Selecciona primero una clase para crear categoría.');
        this.$emit('touch-class-option');
        return;
      }

      this.showNewCategoryInput = !this.showNewCategoryInput;
      if (!this.showNewCategoryInput) {
        this.newCategoryName = '';
      }
    },
    async saveNewCategory() {
      if (this.disabled) {
        return;
      }

      const name = String(this.newCategoryName || '').trim();
      const classId = toPositiveOptionId(this.classOption);

      if (classId <= 0) {
        this.$toast?.error('Selecciona primero una clase para crear categoría.');
        return;
      }

      if (name === '' || this.creatingCategory) {
        return;
      }

      this.creatingCategory = true;
      try {
        const response = await expenseCategoriesService.store({ name, class_id: classId });
        const created = extractCreatedItem(response);
        const createdOption = normalizeCatalogOptions([created], { useCodeInLabel: false })[0] || null;

        if (createdOption) {
          this.categoryOptions = [...this.categoryOptions, createdOption];
          this.allCategoryOptions = [...this.allCategoryOptions, createdOption];
          this.$emit('update:categoryOption', createdOption);
        }

        this.showNewCategoryInput = false;
        this.newCategoryName = '';
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'No fue posible crear la categoría.';
        this.$toast?.error(message);
      } finally {
        this.creatingCategory = false;
      }
    },
    cancelNewCategory() {
      if (this.disabled) {
        return;
      }

      this.showNewCategoryInput = false;
      this.newCategoryName = '';
    },
  },
};
</script>

<style scoped>
.app-class-category-selector {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.app-class-category-selector__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}

.app-class-category-selector__label-row span {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}

.app-class-category-selector__link {
  border: 0;
  background: transparent;
  color: #0070cd;
  font-size: 12px;
  font-weight: 600;
  padding: 0;
}

.app-class-category-selector__inline {
  margin-top: 10px;
  border: 1px dashed rgba(153, 152, 153, 0.45);
  border-radius: 8px;
  padding: 12px;
  display: grid;
  gap: 10px;
}

.app-class-category-selector__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.app-class-category-selector__actions .app-header-btn {
  min-height: 38px;
  padding: 0 16px;
  border-radius: 7px;
}

@media (max-width: 900px) {
  .app-class-category-selector {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
